import { inject, Injectable } from '@angular/core';
import {
  ChartAxisBehavior,
  chartAxisBehaviorMap,
  ChartBehavior,
  chartBehaviorMap,
  ChartInteractionBehavior,
  chartInteractionBehaviorMap,
  RegressionData,
  ScatterData,
  WidgetBehavior,
  widgetBehaviorMap,
  WidgetConfig,
  WidgetType,
} from '../grid-widget-config.service';
import { ColorsetStore } from '../../stores/colorset/colorset.state';
import regression, { DataPoint } from 'regression';
import {
  baseColors,
  generateColor,
  getTextColorBasedOnBackground,
  hexToRgba,
} from '../../utility/color.utility';
import { TooltipHelperService } from './tooltip-helper.service';
import { GridStore } from '../../stores/grid/grid.state';

@Injectable({
  providedIn: 'root',
})
export class WidgetHelperService {
  constructor(private tooltipHelperService: TooltipHelperService) {}

  readonly colorsetStore = inject(ColorsetStore);
  readonly gridStore = inject(GridStore);

  /**
   * Retrieves a color from a specified colorset, processed using the given parameters.
   *
   * @param {string} colorsetId - The identifier of the colorset to retrieve the color from.
   * @param {number} idx - The index of the desired color in the colorset or palette.
   * @param {number} [opacity=0.8] - The opacity level for the color, defaults to 0.8 if not provided.
   * @param {boolean} [invert=false] - Flag to determine if the palette should be inverted, defaults to false.
   * @return {string} The color in RGBA format based on the specified parameters.
   */
  getColor(
    colorsetId: string,
    idx: number,
    opacity = 0.8,
    invert = false
  ): string {
    const colorset = this.colorsetStore.getById(colorsetId);

    // Determine colors to use based on whether colorset is available
    const colors = colorset ? [...colorset.colors] : baseColors;

    // Generate and optionally reverse the palette
    const palette = this.getProcessedPalette(colors, idx + 1, invert);

    // Return the requested color with the specified opacity
    return hexToRgba(palette[idx], opacity);
  }

  /**
   * Generates and processes a color palette based on the provided parameters.
   *
   * @param {string[]} colors - An array of base color strings to generate the palette from.
   * @param {number} requiredCount - The total number of colors required in the processed palette.
   * @param {boolean} invert - A flag indicating whether the generated palette should be inverted (reversed).
   * @return {string[]} An array of processed color strings in the adjusted palette.
   */
  private getProcessedPalette(
    colors: string[],
    requiredCount: number,
    invert: boolean
  ): string[] {
    const palette = this.generateFullPalette(colors, requiredCount);
    return invert ? palette.reverse() : palette;
  }

  /**
   * Generates a full palette of colors by filling in additional colors
   * to meet the required count.
   *
   * @param {string[]} existingColors - An array of existing color strings to start the palette.
   * @param {number} requiredCount - The total number of colors required in the palette.
   * @return {string[]} An array of color strings representing the full palette.
   */
  private generateFullPalette(
    existingColors: string[],
    requiredCount: number
  ): string[] {
    const fullColors = [...existingColors]; // Start with existing colors
    const existingCount = fullColors.length;

    // Generate new colors for missing indices
    for (let i = existingCount; i < requiredCount; i++) {
      const newColor = generateColor(fullColors, i);
      fullColors.push(newColor);
    }

    return fullColors;
  }

  /**
   * Retrieves a list of colors from the specified colorset or generates a default palette.
   *
   * @param {string} colorsetId - The identifier for the colorset to retrieve.
   * @param {boolean} [invert=false] - Whether to invert the order of the colors in the palette.
   * @param {number} [opacity=0.8] - The opacity to apply to the colors in the palette (0 to 1).
   * @param {number} [requiredCount=0] - The required minimum number of colors in the resulting palette.
   * @return {string[]} An array of colors in RGBA format, adjusted for the specified opacity.
   */
  getColors(
    colorsetId: string,
    invert = false,
    opacity = 0.8,
    requiredCount = 0
  ): string[] {
    const colorset = this.colorsetStore.getById(colorsetId);

    let colors = colorset ? [...colorset.colors] : baseColors;

    // Ensure the palette has enough colors for the required count (e.g., segments of the chart)
    if (requiredCount > 0 && colors.length < requiredCount) {
      // Dynamically generate additional colors if the palette is too small
      colors = this.generateFullPalette(colors, requiredCount);
    }

    // Invert the palette if requested
    if (invert) {
      colors = colors.reverse();
    }

    // Map colors to the desired opacity
    return colors.map(color => hexToRgba(color, opacity));
  }

  /**
   * Retrieves the background color(s) for a chart based on its behavior type.
   *
   * @param {string} type - The type of the chart that determines its background color behavior.
   * @param {string} colorsetId - The identifier for the colorset to retrieve colors from.
   * @param {boolean} [invertColors=false] - Whether to invert the colors from the colorset.
   * @param {number} idx - The index of the color to retrieve within the colorset.
   * @param {number} [opacity=0.8] - The opacity level of the background color(s), provided as a value between 0 and 1.
   * @param {number} [segmentCount=0] - The number of segments for segment-base chart-types like pie-charts
   * @return {string|string[]} Returns a single color or an array of colors based on the chart's behavior and parameters.
   */
  getChartBackgroundColor(
    type: string,
    colorsetId: string,
    invertColors = false,
    idx: number,
    opacity = 0.8,
    segmentCount = 0
  ): string | string[] {
    switch (chartBehaviorMap[type]) {
      case ChartBehavior.SINGLE_COLOR_PER_SEGMENT:
        return this.getColors(colorsetId, invertColors, opacity, segmentCount);
      case ChartBehavior.SINGLE_COLOR_PER_DATASET:
        return this.getColor(colorsetId, idx, opacity, invertColors);
      default:
        return `rgba(120,120,120,${opacity})`;
    }
  }

  /**
   * Modifies the chart's background colors based on the provided parameters.
   *
   * @param {string | string[]} backgroundColor - The background color(s) of the chart. It can be a single color or an array of colors.
   * @param {number | number[]} idx - The index or indices where the color modification should be applied.
   * @param {{ color: string, opacity: number }} options - Contains the new color and the opacity to apply to the specified indices.
   * @param {number} [multiplyColor=0] - If greater than 0, multiplies the backgroundColor into an array of the specified size.
   * @return {string | string[]} The modified background color(s) as a string or an array of strings.
   */
  modifyChartColors(
    backgroundColor: string | string[],
    idx: number | number[],
    options: {
      color: string;
      opacity: number;
    },
    multiplyColor = 0
  ): string | string[] {
    let result: string | string[];

    // If multiplyColor is set and backgroundColor is not an array, make it an array
    if (multiplyColor > 0 && !Array.isArray(backgroundColor)) {
      result = Array(multiplyColor).fill(backgroundColor);
    } else {
      result = [...backgroundColor];
    }

    // If a result is now an array, overwrite the specified indices
    if (Array.isArray(result)) {
      const indices = Array.isArray(idx) ? idx : [idx];
      for (let i = 0; i < result.length; i++) {
        const color = result[i];
        result[i] = hexToRgba(
          indices.includes(i) ? options.color : color || '#000000',
          options.opacity
        );
      }
    } else {
      // If a result is still a single color and idx matches, replace it
      if (idx === 0 || (Array.isArray(idx) && idx.includes(0))) {
        //result = hexToRgba(options.color, options.opacity);
        result = hexToRgba(options.color, options.opacity);
      }
    }

    return result;
  }

  /**
   * Determines whether a widget type is classified as a content widget.
   *
   * @param {string} type - The type of the widget to be evaluated.
   * @return {boolean} Returns true if the widget type is classified as a content widget, otherwise false.
   */
  isContentWidget(type: string): boolean {
    switch (widgetBehaviorMap[type]) {
      case WidgetBehavior.DATA_WIDGET:
        return false;
      case WidgetBehavior.CONTENT_WIDGET:
        return true;
      case WidgetBehavior.PLACEHOLDER:
        return false;
    }
  }

  /**
   * Determines if the specified widget type is classified as a placeholder widget.
   *
   * @param {string} type - The type of the widget to check.
   * @return {boolean} Returns true if the widget type is a placeholder; otherwise, false.
   */
  isPlaceholderWidget(type: string): boolean {
    switch (widgetBehaviorMap[type]) {
      case WidgetBehavior.DATA_WIDGET:
        return false;
      case WidgetBehavior.CONTENT_WIDGET:
        return false;
      case WidgetBehavior.PLACEHOLDER:
        return true;
    }
  }

  /**
   * Determines if the given widget type represents a single-axis chart.
   *
   * @param {WidgetType} type - The type of the widget to check.
   * @return {boolean} True if the widget type is a single-axis chart, otherwise false.
   */
  isSingleAxisChart(type: WidgetType): boolean {
    return chartBehaviorMap[type] === ChartBehavior.SINGLE_COLOR_PER_DATASET;
  }

  /**
   * Determines if the given widget type is classified as a data widget.
   *
   * @param {string} type - The type of the widget to be evaluated.
   * @return {boolean} Returns true if the widget type is a data widget, false otherwise.
   */
  isDataWidget(type: string): boolean {
    switch (widgetBehaviorMap[type]) {
      case WidgetBehavior.DATA_WIDGET:
        return true;
      case WidgetBehavior.CONTENT_WIDGET:
        return false;
      case WidgetBehavior.PLACEHOLDER:
        return false;
    }
  }

  /**
   * Generates and displays an HTML legend for the given chart type.
   *
   * @param {string} type The type of the chart for which the legend is being displayed.
   * @return {boolean} Returns true if the legend is displayed; otherwise, returns false.
   */
  displayHtmlLegend(type: string): boolean {
    switch (chartAxisBehaviorMap[type]) {
      case ChartAxisBehavior.DATA_AXIS_BOTH:
        return false;
      default:
        return true;
    }
  }

  /**
   * Generates and returns the base configuration options for a Chart.js widget based on the provided type and configuration.
   *
   * @param {WidgetType} type - The type of the widget (e.g., bar, line, scatter).
   * @param {WidgetConfig} [config] - Optional configuration object containing widget-specific customization settings.
   * @return {any} The configured base options enhanced with additional widget-specific customizations.
   */
  getBaseOptions(type: WidgetType, config?: WidgetConfig): any {
    if (!config) return;
    const baseOptions: any = {
      responsive: true,
      animation: {
        lazy: true,
        duration: 1000, // Animation duration in ms
        easing: 'easeOutQuart', // Easing function for smooth animations
      },
      maintainAspectRatio: false,
      aspectRatio: 2,
      interaction: {
        intersect:
          chartInteractionBehaviorMap[type] === ChartInteractionBehavior.SHAPE, // Allow tooltips to show when not directly intersecting
        mode: 'nearest', // Find the nearest point
      },
      plugins: {
        legend: {
          display: false, // Disable default legend
          labels: {
            filter: (item: any) => item.text !== '',
            usePointStyle: true,
          },
        },
        datalabels: {
          color: (context: any) => {
            if (
              (!config.stacked && type === 'bar') ||
              config.featureOpacity < 0.8 ||
              type === 'line'
            )
              return '#222222';
            // Get the background color of the dataset
            const datasetBackgroundColor = context.dataset.backgroundColor;
            const dataIndex = context.dataIndex; // Current data point index
            let backgroundColor;

            // Handle case if backgroundColor is array (e.g., per data point)
            if (Array.isArray(datasetBackgroundColor)) {
              backgroundColor = datasetBackgroundColor[dataIndex];
            } else {
              backgroundColor = datasetBackgroundColor;
            }

            // Use your existing method to determine text color based on background
            return getTextColorBasedOnBackground(backgroundColor) ===
              'text-black'
              ? '#222222'
              : '#fefefe';
          },
          clamp: true,
          anchor: 'end',
          offset: 0,
          align: !config.stacked && type === 'bar' ? 'end' : 'start',
          //display: config?.displayValues || false,
          //display: (context: any) => {
          //  return !(context.dataset.comparisonLine || !config?.displayValues);
          //},
          display: (context: any) => {
            if (context.dataset.comparisonLine || !config?.displayValues)
              return false;

            const chart = context.chart; // The chart instance
            const type = chart.config.type; // Type of the chart (pie, bar, etc.)

            if (config?.displayValues && !config.stacked && type === 'bar')
              return true;

            const dataset = context.dataset; // The dataset
            const value = dataset.data[context.dataIndex]; // The value at the data point

            // Type-specific logic to determine visibility
            switch (type) {
              case 'bar': {
                if (!value) return false;
                if (config.mode === 'horizontal') {
                  const scale = chart.scales.x; // X-axis scale for horizontal bar
                  const barWidth = Math.abs(
                    scale.getPixelForValue(value) - scale.getBasePixel()
                  ); // Calculate bar width
                  const labelWidth =
                    (Math.round(value).toString().length + 3) * 5; // Approx pixel width per character
                  return barWidth > labelWidth; // Only show if bar width is greater than label width
                } else {
                  const scale = chart.scales.y; // Y-axis scale for vertical bar
                  const barHeight = Math.abs(
                    scale.getPixelForValue(value) - scale.getBasePixel()
                  ); // Calculate bar height
                  const labelHeight = 14; // Approx size of the text
                  return barHeight > labelHeight; // Show only if there's enough bar to display
                }
              }
              case 'pie':
              case 'doughnut': {
                const meta = chart.getDatasetMeta(context.datasetIndex);

                const total = dataset.data.reduce(
                  (acc: number, curr: number) => Number(acc) + Number(curr),
                  0
                ); // Sum of all values in the dataset

                // Retrieve the exact value of the current slice using context.dataIndex
                const value = Number(dataset.data[context.dataIndex]);

                if (
                  !value ||
                  !total ||
                  meta.hidden ||
                  meta.data[context.dataIndex].hidden
                )
                  return false; // Safeguard against undefined values

                const sliceAngle = (value / total) * (2 * Math.PI); // Calculate slice angle
                const chartArea = chart.chartArea; // The entire chart canvas area
                const sliceSize = chartArea.width * sliceAngle; // Approximate pixel size of the slice

                return sliceSize > 30; // Only display labels if the slice is large enough
              }
              case 'polarArea': {
                const scale = chart.scales.r; // Radial scale of the polar chart
                const value = Number(dataset.data[context.dataIndex]); // The current value of the datapoint

                // Get pixel position for the dataset value
                const outerRadius = scale.getPixelForValue(value); // The radius corresponding to the value
                const innerRadius = scale.getBasePixel(); // The base (inner) circle of the polar area chart

                const segmentThickness = outerRadius - innerRadius; // Thickness of the polar segment

                return segmentThickness > 30; // Display label only if the segment is thick enough
              }
              default:
                return true; // Default: always display labels
            }
          },
          rotation: (context: any) => {
            if (config.mode === 'horizontal') return 0;
            const chart = context.chart;

            // `scale` represents the x-axis (or y-axis in the horizontal bar chart)
            const scale = chart.scales?.x;
            if (!scale) {
              return 0; // Safe fallback
            }

            // Get bar width from the dataset settings
            const datasetCount = config.stacked
              ? 1
              : chart.data.datasets.length; // Number of datasets
            const barPercentage =
              chart.config.options?.scales?.x?.barPercentage || 0.9; // Default 0.9
            const categoryPercentage =
              chart.config.options?.scales?.x?.categoryPercentage || 0.8; // Default 0.8

            // Compute the total gap per category (i.e., pixel range for one bar group)
            const categoryGap =
              Math.abs(scale.getPixelForValue(1) - scale.getPixelForValue(0)) ||
              1;

            // Effective bar width (adjust for multiple datasets and percentages)
            const barWidth =
              (categoryGap * categoryPercentage * barPercentage) / datasetCount;

            // Approximate label width
            const value = context.dataset.data[context.dataIndex];
            const labelWidth = (Math.round(value).toString().length + 3) * 5; // Rough estimation (adjust multiplier for font size)

            // Rotate if label width exceeds bar width
            return labelWidth > barWidth ? 270 : 0;
          },
          font: {
            size: 11,
          },
          formatter: (value: number, context: any) => {
            return this.tooltipHelperService.getFormattedTooltipValue(
              value,
              type,
              config,
              context,
              'symbol'
            );
          },
        },
        tooltip: {
          enabled: false, // Disable the default tooltip
          external: (context: any) => {
            // Call the service method to render the HTML tooltip
            this.tooltipHelperService.createOrUpdateTooltip(
              context.tooltip,
              'chart-js--tooltip-container',
              type,
              config,
              this.isSingleAxisChart(type)
            );
          },
        },
      },
    };

    return this.enhanceChartJsOptions(baseOptions, type, config);
  }

  /**
   * Enhances the Chart.js options object based on the provided widget type and configuration.
   *
   * @param {any} options - The base Chart.js options object to be enhanced.
   * @param {WidgetType} type - The type of widget being rendered, determining the axis behavior and modifications.
   * @param {WidgetConfig} [config] - Optional widget configuration that provides specific details such as inner radius, axis settings, mode, etc.
   * @return {any} - The modified Chart.js options object with enhancements applied based on the widget configuration.
   */
  enhanceChartJsOptions(
    options: any,
    type: WidgetType,
    config?: WidgetConfig
  ): any {
    if (!config) return options;
    const axisBehavior = chartAxisBehaviorMap[type];

    switch (axisBehavior) {
      case ChartAxisBehavior.DATA_AXIS_NONE:
        options.cutout = config.innerRadius
          ? `${config.innerRadius * 100}%`
          : 0;
        break;
      case ChartAxisBehavior.DATA_AXIS_SINGLE:
        options.indexAxis = config.mode === 'horizontal' ? 'y' : 'x';
        options.borderRadius = 2;

        if (!options.scales) options.scales = { x: {}, y: {} };
        options.scales.x.stacked = config.stacked;
        options.scales.y.stacked = config.stacked;

        if (config.axis && config.chartData) {
          options.scales.x.min =
            config.axis.x.min === 0
              ? 0
              : config.axis.x.min != null &&
                  config.chartData.minValue < config.axis.x.min
                ? config.chartData.minValue
                : (config.axis.x.min ?? null);
          options.scales.x.max =
            config.axis.x.max === 0
              ? 0
              : config.axis.x.max != null &&
                  config.chartData.maxValue > config.axis.x.max
                ? config.chartData.maxValue
                : (config.axis.x.max ?? null);
          options.scales.y.min =
            config.axis.y.min === 0
              ? 0
              : config.axis.y.min != null &&
                  config.chartData.minValue < config.axis.y.min
                ? config.chartData.minValue
                : (config.axis.y.min ?? null);
          options.scales.y.max =
            config.axis.y.max === 0
              ? 0
              : config.axis.y.max != null &&
                  config.chartData.maxValue > config.axis.y.max
                ? config.chartData.maxValue
                : (config.axis.y.max ?? null);
          options.scales.x.title = {
            display: !!config.axis.x.label,
            text: config.axis.x.label || '',
          };
          options.scales.y.title = {
            display: !!config.axis.y.label,
            text: config.axis.y.label || '',
          };
        }
        options.scales.x.ticks = options.scales.y.ticks = {
          callback: function (value: string, index: number, values: any) {
            // Split label into multiple lines
            const label = this.getLabelForValue(value); // Get the label value
            return label ? String(label).split(': ') : label; // Split by " - " and return an array
          },
        };

        options.layout = {
          padding: {
            top:
              type === 'bar' && !config.stacked && config.mode === 'vertical'
                ? 30
                : 0,
            bottom: 0,
            left: 0,
            right:
              type === 'bar' && !config.stacked && config.mode === 'horizontal'
                ? 5
                : 0,
          },
        };
        break;
      case ChartAxisBehavior.DATA_AXIS_BOTH:
        if (!options.scales) options.scales = { x: {}, y: {} };

        // eslint-disable-next-line no-case-declarations
        const xLabel = config.axis?.x.label || config.xAxisLabel || '';
        // eslint-disable-next-line no-case-declarations
        const yLabel = config.axis?.y.label || config.yAxisLabel || '';

        options.scales.x = {
          title: {
            display: !!xLabel,
            text: xLabel,
          },
          min: config.axis?.x.min ?? null,
          max: config.axis?.x.max ?? null,
        };
        options.scales.y = {
          title: {
            display: !!yLabel,
            text: yLabel,
          },
          min: config.axis?.y.min ?? null,
          max: config.axis?.y.max ?? null,
        };
        break;
    }

    return options;
  }

  /**
   * Generates and returns configuration options for creating a comparison line or bar in a chart.
   *
   * @param {Array<number|null>} data - An array of data points used to plot the line or bar chart. Null values indicate gaps in the data.
   * @param {Object} options - Configuration options used to customize the appearance and behavior of the line or bar.
   * @param {string} options.color - The color used for the line or bar.
   * @param {string} options.tooltip - The label or tooltip associated with the line or bar.
   * @param {boolean} options.asBar - Determines whether to render the data as a bar instead of a line.
   * @param {number} options.legendIndex - The index of the dataset in the chart legend.
   * @param {number} options.compIndex - A custom index value used to compute the line's border dash pattern.
   *
   * @return {Object} An options object for a dataset, configured for either a line or bar representation based on the input.
   */
  getComparisonLineOptions(
    data: (number | null)[],
    dataKeys: (string | null)[],
    options: any
  ): any {
    const borderDash = [5 * options.compIndex, 5 * options.compIndex];
    return options.asBar
      ? {
          data: data,
          backgroundColor: options.color,
          borderColor: options.color,
          label: options.tooltip,
        }
      : {
          dataKeys: dataKeys,
          hideInLegend: true,
          comparisonLine: true,
          legendIndex: options.legendIndex,
          compIndex: options.compIndex,
          type: 'line',
          data: data,
          label: options.tooltip,
          labelKey: options.labelKey,
          customTooltip: options.tooltip,
          borderColor: options.color,
          borderWidth: 2,
          borderDash: borderDash,
          fill: false,
          pointRadius: 3,
          pointHitRadius: 10,
          tension: 0,
          spanGaps: true,
        };
  }

  /**
   * Determines whether the given chart type is axis-based.
   *
   * @param {string} type - The type of the chart to check.
   * @return {boolean} Returns true if the chart type is axis-based; otherwise, false.
   */
  isAxisBasedType(type: string): boolean {
    return ['scatter', 'bubble'].includes(type);
  }

  /**
   * calculates regression line and r2 value by scatter-points using regression-package
   * @param scatterData
   */
  calcRegression(scatterData: ScatterData[]): RegressionData {
    const points: DataPoint[] = scatterData.map((point: ScatterData) => [
      point.x,
      point.y,
    ]);
    const regressionResult = regression.linear(points);

    return {
      line: regressionResult.points.map(([x, y]) => ({
        x,
        y,
      })),
      r2: regressionResult.r2,
    };
  }
}
